import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import {
  SESSION_KINDS,
  countSession,
  precheckLoad,
  precheckReceive,
  viewScan,
  type AddScanResult,
  type OpsCarrierItem,
  type OpsSession,
  type ScanTone,
} from '@ecobox/firebase';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  ScanInput,
  ScannerView,
  Textarea,
  TimeInState,
  scanFeedback,
  useToast,
  type BadgeTone,
  type ScanVerdict,
} from '@ecobox/ui';
import { useBranchCargo, useCarrierItems } from '../hooks/useOps';
import { useWakeLock } from '../hooks/useWakeLock';
import { loadBarcodeDetector } from '../scanner/detector';
import { receiveProgress } from '../session/progress';
import { useServices } from '../services';
import { useAllSessions, useLedgerById, usePermissions, useSessionEngine } from '../sessions';

const TONE_BADGE: Record<ScanTone, BadgeTone> = {
  ok: 'success',
  wait: 'warning',
  warn: 'warning',
  bad: 'danger',
};
const VERDICT: Record<AddScanResult['tone'], ScanVerdict> = { ok: 'ok', warn: 'warn', bad: 'bad' };
const PAGE = 100;

export function SessionPage() {
  const { id } = useParams();
  const session = useAllSessions().find((s) => s.id === id);
  // A handover has its own screen, with the sign step.
  if (session?.kind === 'load_handover') return <Navigate to={`/handover/${session.id}`} replace />;
  // A driver's load scan and scan-back have their own screens, next to the run.
  if (session?.kind === 'run_load')
    return <Navigate to={`/run/${session.targetId}/load`} replace />;
  if (session?.kind === 'run_return')
    return <Navigate to={`/run/${session.targetId}/end`} replace />;
  if (!session) {
    return (
      <EmptyState
        title="This session is not on this phone"
        description="It may have been finished, or it was started on another phone."
        action={
          <Link to="/today">
            <Button size="touch">Back to Today</Button>
          </Link>
        }
      />
    );
  }
  return <SessionScreen session={session} />;
}

function SessionScreen({ session }: { session: OpsSession }) {
  const info = SESSION_KINDS[session.kind];
  const engine = useSessionEngine();
  const { writer } = useServices();
  const ledger = useLedgerById();
  const perms = usePermissions();
  const toast = useToast();
  const navigate = useNavigate();
  const finished = session.finishedAt !== undefined;
  const loading = info.direction === 'load';

  const [camera, setCamera] = useState(false);
  const [last, setLast] = useState<AddScanResult | null>(null);
  const [stage, setStage] = useState<'scanning' | 'finishing'>('scanning');
  const [shown, setShown] = useState(PAGE);

  useWakeLock(!finished);

  const cargo = useBranchCargo(session.branchId, loading && !finished);
  const items = useCarrierItems(
    info.carrier === 'trip' ? 'trips' : 'containers',
    loading ? null : session.targetId,
  );
  const expected = useMemo(
    () => new Map<string, OpsCarrierItem>(items.data.map((i) => [i.barcode, i])),
    [items.data],
  );

  const counts = countSession(session, ledger);
  const progress = loading ? null : receiveProgress(items.data, session, ledger);

  const handleScan = (raw: string): ScanVerdict => {
    const barcode = raw.trim().toUpperCase();
    const box = cargo.boxes.get(barcode);
    const precheck = loading
      ? precheckLoad({
          kind: info.carrier === 'container' ? 'container' : 'trip',
          carrierId: session.targetId,
          branchId: session.branchId,
          barcode,
          box,
          unpaidShipment: box ? cargo.unpaid.get(box.trackingNo) : undefined,
          at: new Date().toISOString(),
        })
      : precheckReceive({ expected, barcode });
    const result = engine.addScan(session.id, barcode, precheck);
    setLast(result);
    return VERDICT[result.tone];
  };

  const rows = [...session.scans].reverse();
  const rejectedBatches = session.batches.filter(
    (b) =>
      b.count > 1 &&
      ledger.get(b.commandId)?.state === 'rejected' &&
      session.scans.some((s) => s.commandId === b.commandId),
  );

  const send = (
    type: 'departTrip' | 'closeTrip' | 'closeContainer' | 'completeContainerReceipt',
    payload: Record<string, unknown>,
  ) => {
    try {
      const sent = writer.send(type, payload);
      engine.markClosed(session.id, sent.id);
    } catch (err) {
      toast.show({
        title: 'Not sent',
        message: err instanceof Error ? err.message : String(err),
        tone: 'danger',
      });
    }
  };

  if (finished) return <FinishedView session={session} onBack={() => void navigate('/today')} />;

  return (
    <div className="ops-stack">
      <div data-tour="session-head">
        <h1 className="ops-title">{info.label}</h1>
        <p className="ops-muted" style={{ margin: 0 }}>
          {session.targetLabel} · started <TimeInState since={session.startedAt} suffix="ago" />
        </p>
      </div>

      {loading ? (
        <div className="ops-tiles" data-tour="session-counters">
          <Tile n={counts.good} label="scanned" tone="ok" />
          <Tile
            n={counts.unsent}
            label="not sent yet"
            tone={counts.unsent > 0 ? 'wait' : undefined}
          />
          {counts.held > 0 ? <Tile n={counts.held} label="held back" tone="bad" /> : null}
          {counts.refused > 0 ? <Tile n={counts.refused} label="refused" tone="bad" /> : null}
        </div>
      ) : progress ? (
        <>
          <div className="ops-tiles" data-tour="session-counters">
            <Tile n={progress.expected} label="expected" />
            <Tile n={progress.scanned} label="scanned" tone="ok" />
            <Tile
              n={progress.missing}
              label="missing"
              tone={progress.missing > 0 ? 'bad' : undefined}
            />
            <Tile n={progress.extra} label="extra" tone={progress.extra > 0 ? 'wait' : undefined} />
          </div>
          {items.error ? (
            <p role="alert" className="ops-error">
              {items.error}
            </p>
          ) : items.data.length === 0 && !items.loading ? (
            <p className="ops-muted">
              The list of expected boxes is not on this phone yet. Scans are still saved; the server
              compares them when they sync.
            </p>
          ) : null}
        </>
      ) : null}

      {loading ? (
        <p className="ops-muted" style={{ margin: 0 }}>
          {cargo.ready
            ? `Checking against ${cargo.boxes.size} boxes and ${cargo.unpaid.size} unpaid shipments at ${session.branchId}.`
            : 'Branch data is loading. Until it is here, the server checks every box.'}
        </p>
      ) : null}

      {stage === 'scanning' ? (
        <>
          <div data-tour="scan-field">
            <ScanInput
              onScan={(code) => scanFeedback(handleScan(code))}
              status={
                last ? (last.tone === 'bad' ? 'error' : last.tone === 'ok' ? 'ok' : 'idle') : 'idle'
              }
            />
          </div>
          {last ? (
            <div className="ops-last" data-tone={last.tone} role="status">
              <span className="ops-code">{last.barcode}</span> {last.message}
            </div>
          ) : null}
          <Button
            size="touch"
            variant="cta"
            block
            data-tour="scan-camera"
            onClick={() => setCamera(true)}
          >
            Scan with the camera
          </Button>
        </>
      ) : (
        <FinishPanel
          session={session}
          unsent={counts.unsent}
          good={counts.good}
          missing={progress?.missing ?? 0}
          canManage={perms.has('container.manage')}
          onBack={() => setStage('scanning')}
          onLeave={() => void navigate('/today')}
          onDone={() => {
            engine.complete(session.id);
            void navigate('/today');
          }}
          onSend={send}
        />
      )}

      {session.lastError ? (
        <p role="alert" className="ops-error">
          {session.lastError}
        </p>
      ) : null}

      {rejectedBatches.map((b) => (
        <div key={b.commandId} role="alert" className="ops-last" data-tone="bad">
          <div>
            {b.count} boxes were refused together: {ledger.get(b.commandId)?.lastError?.message}
          </div>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => {
              const n = engine.retrySingly(session.id, b.commandId);
              toast.show({
                title: 'Sent one by one',
                message: `${n} boxes sent separately.`,
                tone: 'info',
              });
            }}
          >
            Send them one by one
          </Button>
        </div>
      ))}

      {rows.length === 0 ? (
        <p className="ops-muted" data-tour="scan-list">
          Nothing scanned yet. Scan a box and it shows here at once.
        </p>
      ) : (
        <ul className="ops-list" aria-label="Scans" data-tour="scan-list">
          {rows.slice(0, shown).map((scan) => {
            const view = viewScan(scan, scan.commandId ? ledger.get(scan.commandId) : undefined);
            return (
              <li
                key={scan.barcode}
                className="ops-row"
                data-tone={view.tone === 'wait' ? 'waiting' : view.tone}
              >
                <div className="ops-row__top">
                  <span className="ops-code">{scan.barcode}</span>
                  <Badge tone={TONE_BADGE[view.tone]}>{view.label}</Badge>
                </div>
                {view.detail ? <div className="ops-detail">{view.detail}</div> : null}
                <div className="ops-row__actions">
                  {scan.status === 'held' ? (
                    <Button
                      size="lg"
                      variant="secondary"
                      onClick={() => engine.releaseHeld(session.id, scan.barcode)}
                    >
                      Send anyway
                    </Button>
                  ) : null}
                  {scan.status === 'held' ||
                  scan.status === 'invalid' ||
                  (loading && (scan.status === 'buffered' || scan.status === 'queued')) ? (
                    <Button
                      size="lg"
                      variant="ghost"
                      onClick={() => engine.removeScan(session.id, scan.barcode)}
                    >
                      {scan.status === 'queued' ? 'Take off' : 'Remove'}
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {rows.length > shown ? (
        <Button size="lg" variant="secondary" onClick={() => setShown(shown + PAGE)}>
          Show more ({rows.length - shown} older)
        </Button>
      ) : null}

      {stage === 'scanning' ? (
        <div className="ops-actionbar" data-tour="finish-scanning">
          <Button
            size="touch"
            variant="primary"
            block
            onClick={() => {
              engine.flush(session.id);
              setStage('finishing');
            }}
          >
            Finish scanning
          </Button>
          {rows.length === 0 ? (
            <Button
              size="lg"
              variant="ghost"
              onClick={() => {
                if (engine.discard(session.id)) void navigate('/today');
              }}
            >
              Cancel this session
            </Button>
          ) : null}
        </div>
      ) : null}

      {camera ? (
        <ScannerView
          loadDetector={loadBarcodeDetector}
          title={`${info.label} · ${session.targetLabel}`}
          count={progress ? progress.scanned : counts.good}
          countLabel={progress ? `of ${progress.expected}` : 'scanned'}
          lastResult={
            last ? { code: last.barcode, tone: VERDICT[last.tone], message: last.message } : null
          }
          onRead={handleScan}
          onClose={() => setCamera(false)}
        />
      ) : null}
    </div>
  );
}

function Tile({ n, label, tone }: { n: number; label: string; tone?: ScanTone | undefined }) {
  return (
    <div className="ops-tile" data-tone={tone}>
      <div className="ops-tile__n">{n}</div>
      <div className="ops-muted">{label}</div>
    </div>
  );
}

type CloseType = 'departTrip' | 'closeTrip' | 'closeContainer' | 'completeContainerReceipt';

function FinishPanel({
  session,
  unsent,
  good,
  missing,
  canManage,
  onBack,
  onLeave,
  onDone,
  onSend,
}: {
  session: OpsSession;
  unsent: number;
  good: number;
  missing: number;
  canManage: boolean;
  onBack: () => void;
  onLeave: () => void;
  onDone: () => void;
  onSend: (type: CloseType, payload: Record<string, unknown>) => void;
}) {
  const [note, setNote] = useState('');
  const [seal, setSeal] = useState('');
  const kind = session.kind;
  const id = session.targetId;

  return (
    <Card title="Finish">
      <div className="ops-stack">
        <p style={{ margin: 0 }}>
          {good} box{good === 1 ? '' : 'es'} scanned.
          {unsent > 0 ? ` ${unsent} could not be sent yet; they will go when you have signal.` : ''}
        </p>

        {kind === 'load_trip' ? (
          <Button
            size="touch"
            variant="cta"
            block
            onClick={() => onSend('departTrip', { tripId: id })}
          >
            Depart the trip now
          </Button>
        ) : null}

        {kind === 'receive_trip' || kind === 'receive_container' ? (
          <>
            {missing > 0 ? (
              <p role="alert" className="ops-error" style={{ margin: 0 }}>
                {missing} expected box{missing === 1 ? ' has' : 'es have'} not been scanned. Closing
                raises each as missing for a person to follow up.
              </p>
            ) : null}
            <Field label="Note (optional)">
              {(p) => (
                <Textarea
                  {...p}
                  value={note}
                  maxLength={300}
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
            </Field>
            <Button
              size="touch"
              variant="cta"
              block
              onClick={() =>
                kind === 'receive_trip'
                  ? onSend('closeTrip', { tripId: id, note: note.trim() })
                  : onSend('completeContainerReceipt', { containerId: id, note: note.trim() })
              }
            >
              {kind === 'receive_trip' ? 'Close the trip' : 'Complete the receipt'}
            </Button>
          </>
        ) : null}

        {kind === 'load_container' ? (
          canManage ? (
            <>
              <Field label="Seal number">
                {(p) => (
                  <Input
                    {...p}
                    size="touch"
                    value={seal}
                    maxLength={30}
                    onChange={(e) => setSeal(e.target.value)}
                  />
                )}
              </Field>
              <Button
                size="touch"
                variant="cta"
                block
                disabled={seal.trim() === ''}
                onClick={() => onSend('closeContainer', { containerId: id, sealNo: seal.trim() })}
              >
                Close and seal the container
              </Button>
            </>
          ) : (
            <>
              <p className="ops-muted" style={{ margin: 0 }}>
                Your boxes are saved. A manager closes and seals the container.
              </p>
              <Button size="touch" variant="cta" block onClick={onDone}>
                I am done loading
              </Button>
            </>
          )
        ) : null}

        <Button size="touch" variant="secondary" block onClick={onBack}>
          Keep scanning
        </Button>
        <Button size="lg" variant="ghost" block onClick={onLeave}>
          Leave it open for now
        </Button>
      </div>
    </Card>
  );
}

function FinishedView({ session, onBack }: { session: OpsSession; onBack: () => void }) {
  const ledger = useLedgerById();
  const counts = countSession(session, ledger);
  const close = session.closeCommandId ? ledger.get(session.closeCommandId) : undefined;
  const info = SESSION_KINDS[session.kind];
  return (
    <div className="ops-stack">
      <h1 className="ops-title">{info.label}</h1>
      <Card title={session.targetLabel}>
        <div className="ops-stack">
          <p style={{ margin: 0 }}>
            {counts.good} box{counts.good === 1 ? '' : 'es'} scanned
            {counts.refused > 0 ? `, ${counts.refused} refused` : ''}.
          </p>
          {close ? (
            <p style={{ margin: 0 }}>
              Final step:{' '}
              {close.state === 'rejected'
                ? `refused by the server. ${close.lastError?.message ?? ''}`
                : close.state === 'pending'
                  ? 'saved on this phone, waiting for signal.'
                  : close.state === 'synced'
                    ? 'sent, waiting for the result.'
                    : 'done.'}
            </p>
          ) : null}
          <Button size="touch" variant="primary" block onClick={onBack}>
            Back to Today
          </Button>
        </div>
      </Card>
    </div>
  );
}
